// Scan reports, notifications, alerting, report templates, email templates API
import { api } from './client';
import type {
  PersonalAlertingSettings,
  CompanyAlertingOverview,
  ProjectRecipientsResponse,
} from '../../types';

// ============================================================================
// SCAN REPORTS API
// ============================================================================

export interface ReportListParams {
  status?: string;
  project_id?: number;
  reviewed_by?: number;
  limit?: number;
  offset?: number;
  page?: number;
}

export const scanReportsAPI = {
  getAll: (params?: ReportListParams) => {
    const queryParams = new URLSearchParams();
    if (params?.status) queryParams.append('status', params.status);
    if (params?.project_id) queryParams.append('project_id', String(params.project_id));
    if (params?.reviewed_by) queryParams.append('reviewed_by', String(params.reviewed_by));
    if (params?.limit) queryParams.append('limit', String(params.limit));
    if (params?.page) queryParams.append('page', String(params.page));
    if (params?.offset) queryParams.append('offset', String(params.offset));
    const queryString = queryParams.toString() ? `?${queryParams.toString()}` : '';
    console.log('📄 Fetching scan reports', queryString);
    return api.get(`/scan-reports${queryString}`);
  },

  getById: (reportId: number) => {
    console.log('📄 Fetching scan report:', reportId);
    return api.get(`/scan-reports/${reportId}`);
  },

  getByScanId: (scanId: number) => {
    console.log('📄 Fetching scan report for scan:', scanId);
    return api.get(`/scans/${scanId}/report`);
  },

  review: (reportId: number, data: any) => {
    console.log('✅ Reviewing scan report:', reportId, data);
    return api.put(`/scan-reports/${reportId}/review`, data);
  },

  sendToClient: (reportId: number, data?: any) => {
    console.log('📤 Sending report to client:', reportId, data);
    return api.post(`/scan-reports/${reportId}/send`, data || {});
  },

  archive: (reportId: number) => {
    console.log('📦 Archiving scan report:', reportId);
    return api.post(`/scan-reports/${reportId}/archive`);
  },

  generate: (scanId: number) => {
    console.log('🔄 Generating scan report for scan:', scanId);
    return api.get(`/scans/${scanId}/report`);
  },
};

// ============================================================================
// NOTIFICATIONS API
// ============================================================================

export interface NotificationListParams {
  type?: string;
  status?: string;
  project_id?: number;
  recipient_id?: number;
  page?: number;
  limit?: number;
}

export const notificationsAPI = {
  getAll: (params?: NotificationListParams) => {
    const queryParams = new URLSearchParams();
    if (params?.type) queryParams.append('type', params.type);
    if (params?.status) queryParams.append('status', params.status);
    if (params?.project_id) queryParams.append('project_id', String(params.project_id));
    if (params?.recipient_id) queryParams.append('recipient_id', String(params.recipient_id));
    if (params?.page) queryParams.append('page', String(params.page));
    if (params?.limit) queryParams.append('limit', String(params.limit));
    const queryString = queryParams.toString() ? `?${queryParams.toString()}` : '';
    console.log('📧 Fetching notification logs', queryString);
    return api.get(`/notifications${queryString}`);
  },

  getStats: () => {
    console.log('📊 Fetching notification statistics');
    return api.get('/notifications/stats');
  },

  retry: (notificationId: number) => {
    console.log('🔄 Retrying notification:', notificationId);
    return api.post(`/notifications/${notificationId}/retry`);
  },
};

// ============================================================================
// ALERTING SYSTEM API
// ============================================================================

export const alertingAPI = {
  // Personal Alerting Settings
  getMySettings: () => {
    console.log('🔔 Fetching my alerting settings');
    return api.get<{ settings: PersonalAlertingSettings }>('/alerting/my-settings');
  },

  updateMySettings: (settings: Partial<PersonalAlertingSettings>) => {
    console.log('🔔 Updating my alerting settings');
    return api.put<{ message: string; settings: PersonalAlertingSettings }>('/alerting/my-settings', settings);
  },

  toggleVacationMode: (enabled: boolean) => {
    console.log('🏖️ Toggling vacation mode:', enabled);
    return api.post<{ message: string; vacation_mode: boolean }>('/alerting/vacation-mode', { enabled });
  },

  testMyPushover: () => {
    return api.post<{ status: string; message: string }>('/alerting/test-pushover', {});
  },

  // Company Alerting Management
  getCompanyOverview: (clientId: number) => {
    console.log('🏢 Fetching company alerting overview for client:', clientId);
    return api.get<{ overview: CompanyAlertingOverview }>(`/clients/${clientId}/alerting`);
  },

  updateCompanySettings: (clientId: number, settings: {
    notifications_enabled: boolean;
    auto_report_generation: boolean;
    preferred_language: string;
  }) => {
    console.log('🏢 Updating company alerting settings for client:', clientId);
    return api.put<{ message: string }>(`/clients/${clientId}/alerting`, settings);
  },

  getCompanyUsers: (clientId: number) => {
    console.log('👥 Fetching company users alerting settings for client:', clientId);
    return api.get<{ users: any[] }>(`/clients/${clientId}/alerting/users`);
  },

  updateCompanyUserAlerting: (clientId: number, userId: number, settings: {
    contact_id?: number;
    notify_on_report?: boolean;
    notify_on_critical?: boolean;
    notify_weekly_digest?: boolean;
    notify_on_exposure?: boolean;
    notify_on_breach?: boolean;
    vacation_mode?: boolean;
  }) => {
    console.log('👤 Updating user alerting settings:', userId, 'for client:', clientId);
    return api.put<{ message: string }>(`/clients/${clientId}/alerting/users/${userId}`, settings);
  },

  // Recipient Selector for Scans/Schedules
  getProjectRecipients: (projectId: number) => {
    console.log('📬 Fetching project recipients for project:', projectId);
    return api.get<ProjectRecipientsResponse>(`/projects/${projectId}/recipients`);
  },
};

// ============================================================================
// REPORT TEMPLATES API
// ============================================================================

export interface ReportTemplate {
  id: number;
  name: string;
  description: string;
  is_default: boolean;
  is_system: boolean;
  created_at: string;
  updated_at: string;
  created_by_id?: number;

  // Header/Footer Configuration
  header_style: string;
  header_background: string;
  header_border_color: string;
  header_border_width: number;
  header_text_color: string;
  footer_background: string;
  footer_text_color: string;
  title_banner_enabled: boolean;
  title_banner_background: string;

  // Logo Configuration
  header_logo_source: string;
  header_logo_position: string;
  header_logo_max_width: number;
  footer_logo_source: string;
  footer_logo_position: string;
  footer_logo_max_width: number;

  // Typography
  primary_font_family: string;
  heading_font_family: string;
  monospace_font_family: string;
  font_size_h1: number;
  font_size_h2: number;
  font_size_h3: number;
  font_size_h4: number;
  font_size_body: number;
  font_size_small: number;
  font_size_caption: number;
  font_size_badge: number;
  font_size_table_header: number;
  font_size_table_cell: number;
  font_weight_heading: number;
  font_weight_body: number;
  font_weight_bold: number;
  line_height_body: number;
  line_height_heading: number;

  // Colors
  primary_color: string;
  secondary_color: string;
  accent_color: string;
  background_color: string;
  text_color: string;
  muted_text_color: string;
  link_color: string;

  // Severity Colors
  critical_color: string;
  critical_bg_color: string;
  high_color: string;
  high_bg_color: string;
  medium_color: string;
  medium_bg_color: string;
  low_color: string;
  low_bg_color: string;
  info_color: string;
  info_bg_color: string;

  // Card Background Colors
  card_bg_color_1: string;
  card_bg_color_2: string;
  card_bg_color_3: string;
  card_bg_color_4: string;
  card_bg_color_5: string;
  card_bg_color_6: string;
  card_bg_color_7: string;
  card_bg_color_8: string;
  card_bg_color_9: string;
  card_bg_color_10: string;

  // Card Styling
  card_border_radius: number;
  card_border_width: number;
  card_border_color: string;
  card_shadow: string;
  card_padding: number;

  // Table Styling
  table_header_bg: string;
  table_header_text_color: string;
  table_border_color: string;
  table_alt_row_bg: string;
  table_hover_bg: string;
  table_cell_padding: number;

  // Badge Styling
  badge_border_radius: number;
  badge_padding_x: number;
  badge_padding_y: number;

  // Section Styling
  section_spacing: number;
  section_header_color: string;
  section_header_border: boolean;

  // Page Layout
  page_margin_top: number;
  page_margin_bottom: number;
  page_margin_left: number;
  page_margin_right: number;
  page_size: string;
  page_orientation: string;

  // Content Options
  show_generated_date: boolean;
  show_page_numbers: boolean;
  show_website: boolean;
  show_company_name: boolean;
  show_company_tagline: boolean;
  company_tagline: string;
  print_footer_on_every_page: boolean;
  avoid_page_break_in_cards: boolean;
  include_executive_summary: boolean;
  include_toc: boolean;

  // Watermark
  watermark_enabled: boolean;
  watermark_text: string;
  watermark_opacity: number;
  watermark_color: string;
  watermark_font_size: number;
  watermark_rotation: number;

  // Confidentiality
  confidentiality_enabled: boolean;
  confidentiality_text: string;
  confidentiality_color: string;
  confidentiality_position: string;

  // Custom CSS
  custom_css: string;

  // Localization
  default_language: string;
  date_format: string;
}

export interface AppLogo {
  id: number;
  name: string;
  filename: string;
  mime_type: string;
  data: string;
  width?: number;
  height?: number;
  is_primary: boolean;
  created_at: string;
  uploaded_by_id?: number;
}

export interface CustomerLogo {
  id: number;
  customer_id: number;
  name: string;
  filename: string;
  mime_type: string;
  data: string;
  width?: number;
  height?: number;
  is_primary: boolean;
  created_at: string;
  uploaded_by_id?: number;
}

interface ReportTemplatesListResponse {
  templates: ReportTemplate[];
  count: number;
}

interface AppLogosListResponse {
  logos: AppLogo[];
}

export const reportTemplatesAPI = {
  list: () => {
    console.log('📄 Fetching report templates');
    return api.get<ReportTemplatesListResponse>('/report-templates');
  },

  getDefaults: () => {
    console.log('📄 Fetching template default values');
    return api.get<ReportTemplate>('/report-templates/defaults');
  },

  get: (id: number) => {
    console.log('📄 Fetching report template:', id);
    return api.get<ReportTemplate>(`/report-templates/${id}`);
  },

  preview: (id: number) => {
    console.log('👁️ Previewing report template:', id);
    return api.get<string>(`/report-templates/${id}/preview`, { responseType: 'text' as any });
  },

  previewCSS: (id: number) => {
    console.log('🎨 Fetching template CSS:', id);
    return api.get<string>(`/report-templates/${id}/css`, { responseType: 'text' as any });
  },

  create: (data: Partial<ReportTemplate>) => {
    console.log('➕ Creating report template:', data.name);
    return api.post<ReportTemplate>('/admin/report-templates', data);
  },

  update: (id: number, data: Partial<ReportTemplate>) => {
    console.log('📝 Updating report template:', id);
    return api.put<ReportTemplate>(`/admin/report-templates/${id}`, data);
  },

  delete: (id: number) => {
    console.log('🗑️ Deleting report template:', id);
    return api.delete(`/admin/report-templates/${id}`);
  },

  duplicate: (id: number, name: string) => {
    console.log('📋 Duplicating report template:', id, 'as', name);
    return api.post<ReportTemplate>(`/admin/report-templates/${id}/duplicate`, { name });
  },

  setDefault: (id: number) => {
    console.log('⭐ Setting default report template:', id);
    return api.post(`/admin/report-templates/${id}/set-default`);
  },

  // App Logos
  listAppLogos: () => {
    console.log('🖼️ Fetching app logos');
    return api.get<AppLogosListResponse>('/admin/app-logos');
  },

  uploadAppLogo: (file: File, name: string) => {
    console.log('📤 Uploading app logo:', name);
    const formData = new FormData();
    formData.append('logo', file);
    formData.append('name', name);
    return api.post<AppLogo>('/admin/app-logos', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },

  deleteAppLogo: (id: number) => {
    console.log('🗑️ Deleting app logo:', id);
    return api.delete(`/admin/app-logos/${id}`);
  },

  setPrimaryAppLogo: (id: number) => {
    console.log('⭐ Setting primary app logo:', id);
    return api.post(`/admin/app-logos/${id}/set-primary`);
  },

  // Customer Logos
  listCustomerLogos: (customerId: number) => {
    console.log('🖼️ Fetching customer logos for:', customerId);
    return api.get<CustomerLogo[]>(`/customers/${customerId}/logos`);
  },

  uploadCustomerLogo: (customerId: number, file: File, name: string) => {
    console.log('📤 Uploading customer logo:', name, 'for:', customerId);
    const formData = new FormData();
    formData.append('logo', file);
    formData.append('name', name);
    return api.post<CustomerLogo>(`/customers/${customerId}/logos`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },

  deleteCustomerLogo: (customerId: number, id: number) => {
    console.log('🗑️ Deleting customer logo:', id);
    return api.delete(`/customers/${customerId}/logos/${id}`);
  },

  setPrimaryCustomerLogo: (customerId: number, id: number) => {
    console.log('⭐ Setting primary customer logo:', id);
    return api.post(`/customers/${customerId}/logos/${id}/set-primary`);
  },
};

// ============================================================================
// EMAIL TEMPLATES API
// ============================================================================

export interface EmailTemplateType {
  id: number;
  code: string;
  name: string;
  description: string;
  category: string;
  available_vars: string;
  sort_order: number;
  is_active: boolean;
  is_system: boolean;
}

export interface EmailTemplateContent {
  id?: number;
  email_type: string;
  language: string;
  subject: string;
  greeting: string;
  intro: string;
  outro: string;
  signature: string;
  template_id?: number;
  is_default?: boolean;
  is_system?: boolean;
}

export interface EmailTemplateVariable {
  name: string;
  description: string;
  example: string;
}

export interface EmailPreviewResponse {
  subject: string;
  html: string;
}

export interface EmailTemplateStats {
  email_types: number;
  content_entries: number;
  email_blocks: number;
  customized_content: number;
  languages: string[];
}

export const emailTemplatesAPI = {
  listTypes: () => {
    console.log('📧 Fetching email template types');
    return api.get<EmailTemplateType[]>('/admin/email-templates/types');
  },

  getType: (idOrCode: string | number) => {
    console.log('📧 Fetching email template type:', idOrCode);
    return api.get<EmailTemplateType>(`/admin/email-templates/types/${idOrCode}`);
  },

  getStats: () => {
    console.log('📊 Fetching email template stats');
    return api.get<EmailTemplateStats>('/admin/email-templates/stats');
  },

  getAllContent: (emailType: string) => {
    console.log('📧 Fetching all content for type:', emailType);
    return api.get<Record<string, EmailTemplateContent>>(`/admin/email-templates/${emailType}`);
  },

  getContent: (emailType: string, lang: string) => {
    console.log('📧 Fetching content:', emailType, lang);
    return api.get<EmailTemplateContent>(`/admin/email-templates/${emailType}/${lang}`);
  },

  updateContent: (emailType: string, lang: string, data: Partial<EmailTemplateContent>) => {
    console.log('📝 Updating email content:', emailType, lang);
    return api.put<EmailTemplateContent>(`/admin/email-templates/${emailType}/${lang}`, data);
  },

  bulkUpdateContent: (emailType: string, data: Record<string, Partial<EmailTemplateContent>>) => {
    console.log('📝 Bulk updating email content:', emailType);
    return api.put(`/admin/email-templates/${emailType}/bulk`, data);
  },

  resetContent: (emailType: string, lang: string) => {
    console.log('🔄 Resetting email content to defaults:', emailType, lang);
    return api.post<EmailTemplateContent>(`/admin/email-templates/${emailType}/${lang}/reset`);
  },

  getVariables: (emailType: string) => {
    console.log('🔤 Fetching variables for:', emailType);
    return api.get<EmailTemplateVariable[]>(`/admin/email-templates/${emailType}/variables`);
  },

  preview: (emailType: string, lang: string = 'en') => {
    console.log('👁️ Previewing email:', emailType, lang);
    return api.post<EmailPreviewResponse>(`/admin/email-templates/${emailType}/preview?lang=${lang}`);
  },

  sendTest: (emailType: string, lang: string = 'en') => {
    console.log('📤 Sending test email:', emailType, lang);
    return api.post<{ message: string; email: string }>(`/admin/email-templates/${emailType}/test?lang=${lang}`);
  },

  getBlocks: (emailType: string) => {
    console.log('🧱 Fetching blocks for email type:', emailType);
    return api.get<any[]>(`/admin/email-templates/${emailType}/blocks`);
  },
};
